import { useState, useEffect, useCallback } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Check,
  X as XIcon,
  Loader2,
  Clock,
  LogOut,
  Mic,
  Inbox,
  CheckCircle2,
  XCircle,
  Mail,
  Phone,
  Building2,
  CalendarDays,
  Globe,
  Instagram,
  ArrowLeft,
  RefreshCw,
  Youtube,
} from "lucide-react";
import { useAuth, authHeaders } from "@/context/AuthContext";
import MondayEventsFeed from "@/components/admin/MondayEventsFeed";
import SyncBadge from "@/components/admin/SyncBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: "pending", label: "Pending", icon: Inbox, color: "#00f0ff" },
  { id: "approved", label: "Approved", icon: CheckCircle2, color: "#22c55e" },
  { id: "declined", label: "Declined", icon: XCircle, color: "#ff0055" },
  { id: "all", label: "All", icon: Mic, color: "#a395c3" },
];

const AdminDashboard = () => {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({ pending: 0, approved: 0, declined: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [selected, setSelected] = useState(null);
  const [ytRefreshing, setYtRefreshing] = useState(false);

  const refreshYoutube = async () => {
    setYtRefreshing(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/youtube/refresh`,
        {},
        { headers: authHeaders(token), timeout: 20000 },
      );
      toast.success(`YouTube feed refreshed · ${data.count} videos loaded`);
    } catch (e) {
      toast.error("Couldn't refresh YouTube feed. Try again in a moment.");
    } finally {
      setYtRefreshing(false);
    }
  };

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const params = tab === "all" ? {} : { status_filter: tab };
      const [list, st] = await Promise.all([
        axios.get(`${API}/guest-requests`, {
          headers: authHeaders(token),
          params,
        }),
        axios.get(`${API}/guest-requests/stats`, {
          headers: authHeaders(token),
        }),
      ]);
      setItems(list.data);
      setStats(st.data);
    } catch (err) {
      toast.error("Couldn't load requests.");
    } finally {
      setLoading(false);
    }
  }, [tab, token]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const decide = async (id, status) => {
    setActing(id + status);
    try {
      const { data } = await axios.patch(
        `${API}/guest-requests/${id}`,
        { status },
        { headers: authHeaders(token) },
      );
      toast.success(
        status === "approved"
          ? "Request approved. Reply to confirm the session."
          : "Request declined.",
      );
      setItems((prev) =>
        tab === "all" ? prev.map((p) => (p.id === id ? data : p)) : prev.filter((p) => p.id !== id),
      );
      setStats((s) => ({
        ...s,
        pending: Math.max(0, s.pending - 1),
        [status]: (s[status] || 0) + 1,
      }));
      setSelected(null);
    } catch {
      toast.error("Action failed. Try again.");
    } finally {
      setActing(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this request permanently?")) return;
    setActing(id + "del");
    try {
      await axios.delete(`${API}/guest-requests/${id}`, {
        headers: authHeaders(token),
      });
      toast.success("Deleted.");
      setItems((p) => p.filter((x) => x.id !== id));
      setSelected(null);
      fetchAll();
    } catch {
      toast.error("Delete failed.");
    } finally {
      setActing(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#05050a] text-white" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-[#05050a]/80 backdrop-blur-md sticky top-0 z-30">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4 min-w-0 flex-1">
            <Link
              to="/admin"
              data-testid="admin-back-hub"
              className="flex items-center gap-2 text-sm text-white/70 hover:text-white flex-shrink-0"
            >
              <ArrowLeft size={14} />
              <span className="hidden md:inline">Hub</span>
            </Link>
            <span className="hidden md:block h-5 w-px bg-white/15" />
            <div className="min-w-0">
              <div className="font-display font-black text-sm tracking-tight truncate">
                SB Admin · Guest requests
              </div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] truncate">
                {user?.email}
              </div>
            </div>
            <span className="hidden md:block h-5 w-px bg-white/15" />
            <Link
              to="/admin/bookings"
              data-testid="admin-to-bookings"
              className="hidden md:inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-[#00f0ff]"
            >
              Bookings →
            </Link>
            <Link
              to="/admin/marketing"
              data-testid="admin-to-marketing"
              className="hidden md:inline-flex items-center gap-1.5 text-xs text-white/60 hover:text-[#00f0ff]"
            >
              Marketing →
            </Link>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <MondayEventsFeed token={token} />
            <button
              onClick={refreshYoutube}
              disabled={ytRefreshing}
              data-testid="admin-refresh-youtube"
              aria-label="Refresh YouTube feed"
              title="Refresh YouTube feed now"
              className="hidden sm:inline-flex items-center gap-1.5 px-3 h-10 rounded-full bg-[#ff0000]/10 border border-[#ff0000]/30 text-[#ff5555] text-xs hover:bg-[#ff0000]/20 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Youtube size={14} className={ytRefreshing ? "animate-pulse" : ""} />
              <span className="hidden md:inline">
                {ytRefreshing ? "Refreshing…" : "Refresh YouTube"}
              </span>
            </button>
            <button
              onClick={fetchAll}
              data-testid="admin-refresh"
              aria-label="Refresh"
              className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10"
            >
              <RefreshCw size={14} className={loading ? "animate-spin" : ""} />
            </button>
            <button
              onClick={handleLogout}
              data-testid="admin-logout"
              aria-label="Logout"
              title="Logout"
              className="w-10 h-10 md:w-auto md:h-auto md:px-4 md:py-2 inline-flex items-center justify-center gap-2 rounded-full bg-white/5 border border-white/10 text-xs hover:bg-white/10"
            >
              <LogOut size={13} />
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8">
          {[
            { k: "pending", l: "Pending", c: "#00f0ff" },
            { k: "approved", l: "Approved", c: "#22c55e" },
            { k: "declined", l: "Declined", c: "#ff0055" },
            { k: "total", l: "Total", c: "#a395c3" },
          ].map((s) => (
            <div
              key={s.k}
              data-testid={`admin-stat-${s.k}`}
              className="rounded-2xl bg-white/[0.03] border border-white/10 p-5"
            >
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3]">
                {s.l}
              </div>
              <div
                className="font-display font-black text-3xl mt-2"
                style={{ color: s.c }}
              >
                {stats[s.k] ?? 0}
              </div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-6">
          {TABS.map((t) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                onClick={() => setTab(t.id)}
                data-testid={`admin-tab-${t.id}`}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all ${
                  active
                    ? "bg-white text-[#05050a]"
                    : "bg-white/[0.04] border border-white/10 text-white/70 hover:text-white"
                }`}
              >
                <t.icon size={13} />
                {t.label}
                {t.id !== "all" && (
                  <span
                    className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
                      active ? "bg-[#05050a] text-white" : "bg-white/10"
                    }`}
                  >
                    {stats[t.id] ?? 0}
                  </span>
                )}
              </button>
            );
          })}
        </div>

        {/* List */}
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] overflow-hidden" data-testid="admin-list">
          {loading ? (
            <div className="p-16 flex items-center justify-center text-white/40">
              <Loader2 className="animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <div className="p-16 text-center">
              <Inbox className="mx-auto w-10 h-10 text-white/30" />
              <div className="font-display text-lg mt-4 text-white/70">
                No {tab !== "all" ? tab : ""} requests yet
              </div>
              <div className="text-sm text-[#a395c3] mt-2">
                When someone submits the Spotlight guest form, it&apos;ll show up
                here.
              </div>
            </div>
          ) : (
            <ul className="divide-y divide-white/5">
              {items.map((r) => (
                <li
                  key={r.id}
                  data-testid={`admin-request-${r.id}`}
                  className="p-5 md:p-6 hover:bg-white/[0.02] transition-colors"
                >
                  <div className="flex flex-col md:flex-row md:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-display font-bold text-lg truncate">
                          {r.name}
                        </span>
                        <StatusPill status={r.status} />
                      </div>
                      <div className="mt-1.5 text-sm text-[#a395c3] flex flex-wrap items-center gap-x-4 gap-y-1">
                        <span className="inline-flex items-center gap-1.5">
                          <Building2 size={12} /> {r.business_name}
                        </span>
                        <span className="inline-flex items-center gap-1.5">
                          <Mail size={12} /> {r.email}
                        </span>
                        {r.preferred_date && (
                          <span className="inline-flex items-center gap-1.5">
                            <CalendarDays size={12} /> {r.preferred_date}
                            {r.preferred_time ? ` · ${r.preferred_time}` : ""}
                          </span>
                        )}
                      </div>
                      <div className="mt-2 text-sm text-white/80 line-clamp-1">
                        <span className="text-[#a395c3] mr-2">Topic:</span>
                        {r.topic}
                      </div>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <button
                        onClick={() => setSelected(r)}
                        data-testid={`admin-view-${r.id}`}
                        className="px-3 py-2 rounded-full bg-white/[0.04] border border-white/10 text-xs hover:bg-white/10"
                      >
                        View details
                      </button>
                      {r.status === "pending" && (
                        <>
                          <button
                            onClick={() => decide(r.id, "approved")}
                            disabled={acting === r.id + "approved"}
                            data-testid={`admin-approve-${r.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/30 text-[#22c55e] text-xs hover:bg-[#22c55e] hover:text-[#05050a] transition-colors disabled:opacity-50"
                          >
                            {acting === r.id + "approved" ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <Check size={13} />
                            )}
                            Approve
                          </button>
                          <button
                            onClick={() => decide(r.id, "declined")}
                            disabled={acting === r.id + "declined"}
                            data-testid={`admin-decline-${r.id}`}
                            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full bg-[#ff0055]/15 border border-[#ff0055]/30 text-[#ff0055] text-xs hover:bg-[#ff0055] hover:text-white transition-colors disabled:opacity-50"
                          >
                            {acting === r.id + "declined" ? (
                              <Loader2 size={12} className="animate-spin" />
                            ) : (
                              <XIcon size={13} />
                            )}
                            Decline
                          </button>
                        </>
                      )}
                    </div>
                  </div>
                  <div className="mt-2.5 text-[11px] text-white/40 flex items-center gap-1.5">
                    <Clock size={10} />
                    Submitted {new Date(r.created_at).toLocaleString()}
                    {r.source_of_truth && r.last_synced_at && (
                      <span className="ml-2">
                        <SyncBadge source={r.source_of_truth} syncedAt={r.last_synced_at} />
                      </span>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </main>

      <RequestDetailModal
        item={selected}
        onClose={() => setSelected(null)}
        onApprove={() => selected && decide(selected.id, "approved")}
        onDecline={() => selected && decide(selected.id, "declined")}
        onDelete={() => selected && remove(selected.id)}
        acting={acting}
      />
    </div>
  );
};

const StatusPill = ({ status }) => {
  const map = {
    pending: { c: "#00f0ff", label: "Pending review" },
    approved: { c: "#22c55e", label: "Approved" },
    declined: { c: "#ff0055", label: "Declined" },
  };
  const m = map[status] || map.pending;
  return (
    <span
      className="inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[10px] uppercase tracking-[0.22em] font-semibold"
      style={{
        color: m.c,
        background: `${m.c}15`,
        border: `1px solid ${m.c}40`,
      }}
    >
      <span
        className="w-1 h-1 rounded-full"
        style={{ background: m.c }}
      />
      {m.label}
    </span>
  );
};

const RequestDetailModal = ({ item, onClose, onApprove, onDecline, onDelete, acting }) => {
  if (!item) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[70] flex items-center justify-center p-4"
      data-testid="admin-detail-modal"
    >
      <button
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/80 backdrop-blur-md"
      />
      <motion.div
        initial={{ y: 30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        className="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-3xl bg-[#0a0612] border border-white/10"
      >
        <div className="sticky top-0 flex items-start justify-between gap-4 px-7 pt-7 pb-5 bg-gradient-to-b from-[#0a0612] to-transparent">
          <div>
            <StatusPill status={item.status} />
            <h3 className="font-display font-black text-2xl mt-2.5 tracking-tight">
              {item.name}
            </h3>
            <div className="text-[#a395c3] text-sm">{item.business_name}</div>
          </div>
          <button
            onClick={onClose}
            data-testid="admin-detail-close"
            className="w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center hover:bg-white/10"
          >
            <XIcon size={16} />
          </button>
        </div>

        <div className="px-7 pb-7 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <DetailRow icon={Mail} label="Email" value={item.email} />
            <DetailRow icon={Phone} label="Phone" value={item.phone || "—"} />
            <DetailRow
              icon={Globe}
              label="Website"
              value={item.business_website || "—"}
            />
            <DetailRow
              icon={Instagram}
              label="Social"
              value={item.social_handle || "—"}
            />
            <DetailRow
              icon={CalendarDays}
              label="Preferred date"
              value={
                item.preferred_date
                  ? `${item.preferred_date}${item.preferred_time ? " · " + item.preferred_time : ""}`
                  : "—"
              }
            />
            <DetailRow
              icon={Clock}
              label="Submitted"
              value={new Date(item.created_at).toLocaleString()}
            />
          </div>

          <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-2">
              Topic
            </div>
            <div className="font-display text-lg">{item.topic}</div>
          </div>

          {item.message && (
            <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-5">
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#a395c3] mb-2">
                Message
              </div>
              <div className="text-sm whitespace-pre-line text-white/90">
                {item.message}
              </div>
            </div>
          )}

          <div className="flex flex-wrap gap-2 pt-2">
            {item.status === "pending" && (
              <>
                <button
                  onClick={onApprove}
                  disabled={acting === item.id + "approved"}
                  data-testid="admin-detail-approve"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#22c55e] text-[#05050a] text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                >
                  <Check size={14} /> Approve
                </button>
                <button
                  onClick={onDecline}
                  disabled={acting === item.id + "declined"}
                  data-testid="admin-detail-decline"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#ff0055] text-white text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                >
                  <XIcon size={14} /> Decline
                </button>
              </>
            )}
            <a
              href={`mailto:${item.email}?subject=Your%20SB%20Spotlight%20guest%20request`}
              data-testid="admin-detail-email"
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 text-sm hover:bg-white/10"
            >
              <Mail size={14} /> Email guest
            </a>
            <button
              onClick={onDelete}
              data-testid="admin-detail-delete"
              className="ml-auto inline-flex items-center gap-2 px-4 py-2.5 rounded-full text-sm text-white/40 hover:text-[#ff0055]"
            >
              Delete
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

const DetailRow = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-3 p-3 rounded-xl bg-white/[0.02] border border-white/5">
    <div className="w-8 h-8 rounded-lg bg-white/5 flex items-center justify-center flex-shrink-0">
      <Icon size={13} className="text-[#00f0ff]" />
    </div>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-[0.24em] text-[#a395c3]">
        {label}
      </div>
      <div className="text-sm text-white mt-0.5 truncate">{value}</div>
    </div>
  </div>
);

export default AdminDashboard;
